import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Autocomplete, OutlinedInput, Tooltip } from "@mui/material";
import { forwardRef, useMemo } from "react";
import { BaseMetadataType } from "../../../Core/Api/Types/base-metadata-type";
import { MetadataInputProps } from "../metadata-input-index";
import { CreateNewDbMetadataValue } from "../../../Core/Utils/asset-object-utils";
import { DbMetadataFieldValueInfos } from "../../../Core/Api/Types/db-metadata-field-value-infos";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useTranslation } from "react-i18next";

const MetadataInputStringOrNumberSingle = forwardRef<any, MetadataInputProps>((props, ref) => {
  const { t } = useTranslation();
  const { onChange, data, value, predefinedValues } = props;
  const tagNameMultiline = ["qcComment"];

  const userCanAddValue = useMemo(
    () =>
      (data.allowPopulate as Boolean) &&
      (UserHasAcl(AclTypes.aclMetadataFieldPopulate) || UserHasAcl(AclTypes.aclSuperUser)),
    []
  );

  const getPopulatedValue = (v: any) =>
    new DbMetadataFieldValueInfos({
      valueObject: CreateNewDbMetadataValue(-500, data.baseType!, data.tagName!, v.value ?? v),
      label: v.value ?? v,
      id: -500,
      value: v.value ?? v,
    });

  return (
    <>
      {data.values?.length && data.values?.length > 0 ? (
        <Autocomplete
          id={data.tagName!}
          // options management
          options={
            userCanAddValue
              ? value?.value
                ? data.values.find(
                    (x) =>
                      x.value === value ||
                      x.value?.toString() === value?.toString() ||
                      x.value === value.value ||
                      x.value?.toString() === value.value?.toString()
                  )
                  ? data.values
                  : [...data.values, getPopulatedValue(value?.value ?? value)]
                : data.values
              : data.values
          }
          disableClearable={data.isMandatory ?? false}
          getOptionLabel={(option: any) => option.label?.toString() ?? option.value?.toString() ?? option?.toString()}
          isOptionEqualToValue={(option: any, value: any) =>
            option.value === value?.value || option.value?.toString() === value?.value?.toString()
          }
          freeSolo={userCanAddValue}
          filterOptions={(x, s) => {
            if (!s || !s.inputValue) return x;
            return x.filter((a) => a.label?.includesAllWords(s.inputValue) || a.value?.includesAllWords(s.inputValue));
          }}
          // value management
          value={
            data.values && data.values?.length > 0
              ? value
                ? data.values.find(
                    (x) =>
                      x.value === value ||
                      x.value?.toString() === value?.toString() ||
                      x.value === value.value ||
                      x.value?.toString() === value.value?.toString()
                  ) ?? data.allowPopulate
                  ? getPopulatedValue(value)
                  : null
                : null
              : value
              ? value.value ?? value
              : ""
          }
          onChange={(e, v) =>
            onChange(
              userCanAddValue
                ? data.values?.find(
                    (x) =>
                      x.value === v ||
                      x.value?.toString() === v ||
                      x.value === v?.value ||
                      x.value?.toString() === v?.value?.toString()
                  ) ?? getPopulatedValue(v)
                : v,
              data.id
            )
          }
          // render input
          popupIcon={<ExpandMoreRoundedIcon />}
          renderInput={(params) => (
            <>
              {userCanAddValue && (
                <Tooltip title={t("tooltip:title.userCanAddValue")}>
                  <AddCircleIcon
                    sx={{ zIndex: 700, position: "absolute", right: 4, bottom: 4, opacity: 0.25 }}
                    fontSize="small"
                  />
                </Tooltip>
              )}
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%" }}
                placeholder={t("field:placeholder.selectAnyValue")}
                onChange={(e) =>
                  userCanAddValue &&
                  onChange(
                    data.values?.find(
                      (x) => x.value === e.target.value || x.value?.toString() === e.target.value.toString()
                    ) ??
                      new DbMetadataFieldValueInfos({
                        valueObject: CreateNewDbMetadataValue(-500, data.baseType!, data.tagName!, e.target.value),
                        label: e.target.value,
                        id: -500,
                        value: e.target.value,
                      }),
                    data.id
                  )
                }
              />
            </>
          )}
          sx={{ width: "100%", position: "relative" }}
        />
      ) : (
        <OutlinedInput
          id={data.tagName!}
          // value management
          value={value ? value.value ?? value : ""}
          onChange={(e) => onChange(e.target.value, data.id)}
          // input rendering
          type={
            data.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
            data.baseType === BaseMetadataType.DbCastsMetadataValueInfos ||
            data.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos
              ? "string"
              : "number"
          }
          sx={{ width: "100%" }}
          placeholder={t("field:placeholder.enterAnyValue")}
          multiline={tagNameMultiline.includes(data.tagName!)}
          rows={tagNameMultiline.includes(data.tagName!) ? 8 : undefined}
        />
      )}
    </>
  );
});

export default MetadataInputStringOrNumberSingle;
